import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { morph } from '@theme/morph';

/**
 * <caldera-sold-out-notice>
 *
 * The message and "Notify Me" button shown for a product withdrawn from sale
 * on the front end while it stays purchasable in Shopify (so Recharge keeps
 * placing recurring orders). Whether a variant is gated is decided
 * server-side — see snippets/caldera-sold-out-gate.liquid.
 *
 * On a single-variant product the server render is the whole feature and this
 * script only opens the Klaviyo form. The rest exists for multi-variant
 * products, where picking a gated variant would otherwise leave a working Add
 * to cart button on screen for the length of Horizon's section refetch.
 *
 * A subscription-required product (data-subscription-required, from
 * snippets/caldera-subscription-gate.liquid) reuses the same swap: the buy
 * button stays for Subscribe & Save and becomes Notify Me while Buy Once is
 * selected, following caldera-subscription.js's change event. The product's
 * own message (data-subscription-message, in the buy row) shows and hides
 * with that button.
 *
 * @typedef {object} CalderaSoldOutNoticeRefs
 * @property {HTMLElement} content - Morphed on variant change.
 * @property {HTMLScriptElement} [gatedVariants] - JSON array of gated variant ids.
 * @extends {Component<CalderaSoldOutNoticeRefs>}
 */
class CalderaSoldOutNotice extends Component {
  // Deliberately no requiredRefs: a missing ref throws out of Component's
  // connectedCallback, which would abort before the listeners are attached. In
  // the quick-add modal this element is morphed into place and its children can
  // land a beat later, so requiring one would silently cost the modal its
  // instant swap. Every ref use below is guarded instead.

  connectedCallback() {
    super.connectedCallback();
    // The Notify Me button renders inside Horizon's button row, not inside this
    // element, so the listener goes on the nearest shared ancestor.
    this.#eventSource()?.addEventListener('click', this.#onClick);
    this.#eventSource()?.addEventListener(StandardEvents.productSelect, this.#onProductSelect);
    this.#eventSource()?.addEventListener('caldera:subscription:change', this.#onSubscriptionChange);
    // The selector may already have announced its choice before this element
    // was defined, so settle the buy box from the DOM as it stands.
    this.#applyGate();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#eventSource()?.removeEventListener('click', this.#onClick);
    this.#eventSource()?.removeEventListener(StandardEvents.productSelect, this.#onProductSelect);
    this.#eventSource()?.removeEventListener('caldera:subscription:change', this.#onSubscriptionChange);
  }

  /**
   * The gated variant ids, read fresh each time rather than cached on connect.
   *
   * In the quick-add modal this element is morphed into place, so its JSON
   * script can land after `connectedCallback` has already run — a cache filled
   * at connect time would stay empty there, silently disabling the instant swap
   * and leaving the modal to wait for the section refetch. Reading on demand
   * also picks up the new list for free when a morph replaces it.
   *
   * @returns {Set<string>}
   */
  #gatedIds() {
    const raw = (this.refs.gatedVariants ?? this.querySelector('[ref="gatedVariants"]'))?.textContent?.trim();
    if (!raw) return new Set();

    try {
      const ids = JSON.parse(raw);
      return Array.isArray(ids) ? new Set(ids.map(String)) : new Set();
    } catch {
      // A malformed list just means no instant swap; the server render on the
      // next variant fetch is still authoritative.
      return new Set();
    }
  }

  /**
   * ProductSelectEvent is fired on the variant picker and bubbles only as far
   * as the section — or the dialog, in the quick-add modal.
   * @returns {Element | null}
   */
  #eventSource() {
    return this.closest('.shopify-section, dialog');
  }

  /** @returns {Document | Element} */
  #scope() {
    return this.closest('#quick-add-dialog') ?? document;
  }

  /** @returns {boolean} */
  get #isGated() {
    return this.dataset.active === 'true';
  }

  /** @returns {boolean} */
  get #requiresSubscription() {
    return this.dataset.subscriptionRequired === 'true';
  }

  /**
   * Whether Subscribe & Save is the selected purchase path. Looked up in this
   * element's own section (or dialog), since a quick-add modal over a PDP puts
   * two selectors on the page. No selector at all means no subscription path
   * either, which fails closed.
   * @returns {boolean}
   */
  #subscribeSelected() {
    const radio = this.#eventSource()?.querySelector('caldera-subscription [data-role="subscribe"]');
    return radio instanceof HTMLInputElement && radio.checked;
  }

  /** @returns {Element[]} */
  #productForms() {
    const { productId } = this.dataset;

    return Array.from(this.#scope().querySelectorAll('product-form-component')).filter(
      (form) => !productId || form.getAttribute('data-product-id') === productId
    );
  }

  /**
   * Points every buy surface at the current gate state.
   *
   * The button swap itself is CSS: `data-caldera-sold-out` on the button row
   * decides whether Horizon's add-to-cart or the Notify Me stand-in is visible,
   * so exactly one shows and neither has to be built here. The express-checkout
   * buttons, the sticky bar and the subscription-required message follow the
   * same state. The subscribe selector goes only with a sold-out variant — on a
   * subscription-required product it is the one thing that must stay, since it
   * holds the working purchase path.
   */
  #applyGate = (variantId = null) => {
    const soldOut = this.#isGated;
    const gated = soldOut || (this.#requiresSubscription && !this.#subscribeSelected());

    // Show the message belonging to the selected variant. Every gated variant's
    // message is already rendered, so this never waits on the section refetch.
    if (variantId != null) {
      for (const message of this.querySelectorAll('.caldera-sold-out-notice__inner')) {
        message.classList.toggle('is-active', message.getAttribute('data-variant-id') === String(variantId));
      }
    }

    for (const form of this.#productForms()) {
      const row = form.querySelector('.product-form-buttons');
      if (row instanceof HTMLElement) row.dataset.calderaSoldOut = String(gated);

      // The subscription-required message explains the Notify Me button, so it
      // follows that button — and yields to a sold-out variant's own message.
      const message = form.querySelector('[data-subscription-message]');
      if (message instanceof HTMLElement) message.toggleAttribute('hidden', !(gated && !soldOut));

      // Lifting the gate must not re-show express checkout for a variant Horizon
      // itself rendered unavailable; the disabled buy button is that signal.
      const buyButton = form.querySelector('[ref="addToCartButton"]');
      const purchasable = !(buyButton instanceof HTMLButtonElement && buyButton.disabled);
      const accelerated = form.querySelector('.accelerated-checkout-block');
      if (accelerated instanceof HTMLElement) accelerated.toggleAttribute('hidden', gated || !purchasable);
    }

    const subscription = this.#eventSource()?.querySelector('caldera-subscription');
    if (subscription instanceof HTMLElement) subscription.style.display = soldOut ? 'none' : '';

    // The sticky bar keeps its own copy of both buttons and swaps them the same
    // way the buy row does, rather than disappearing — a shopper scrolled past
    // the buy box still needs a way onto the waitlist.
    const sticky = this.#scope().querySelector('sticky-add-to-cart');
    if (sticky instanceof HTMLElement) sticky.dataset.calderaSoldOut = String(gated);
  };

  /**
   * Closes the quick-add modal, if this button was clicked inside one.
   *
   * Horizon opens that modal with `showModal()`, which promotes it to the
   * browser's top layer — it paints above every normally-positioned element no
   * matter what z-index they carry, so Klaviyo's popup would open behind it and
   * no amount of stacking on Klaviyo's side can win. Closing the modal first is
   * the only fix, and it reads correctly too: the notify form replaces the
   * quick-add rather than fighting it.
   *
   * Uses the component's own `closeDialog()` rather than `dialog.close()` so
   * the scroll lock is released and focus restored; it resolves once the close
   * animation has finished.
   */
  async #closeEnclosingDialog() {
    const host = /** @type {{ closeDialog?: () => Promise<void> } | null} */ (
      /** @type {unknown} */ (this.closest('#quick-add-dialog'))
    );

    if (typeof host?.closeDialog === 'function') await host.closeDialog();
  }

  /**
   * Opens the Klaviyo signup form. Klaviyo exposes two entry points depending
   * on how far its onsite bundle has initialised, so try the modern one and
   * fall back to the queue the older embed drains.
   * @param {string} formId
   */
  #openKlaviyoForm(formId) {
    const win = /** @type {any} */ (/** @type {unknown} */ (window));

    if (typeof win.klaviyo?.openForm === 'function') {
      win.klaviyo.openForm(formId);
      return;
    }

    if (Array.isArray(win._klOnsite) || win._klOnsite == null) {
      win._klOnsite = win._klOnsite || [];
      win._klOnsite.push(['openForm', formId]);
      return;
    }

    console.warn(`[caldera-sold-out-notice] Klaviyo onsite is not loaded; cannot open form ${formId}.`);
  }

  /** @param {Event} event */
  #onClick = async (event) => {
    const target = event.target instanceof Element ? event.target.closest('[data-notify-me]') : null;
    if (!target) return;

    // Several notice blocks can share a section (PDP plus a quick-add dialog);
    // only the one owning this button's product should respond.
    const form = target.closest('product-form-component');
    const { productId } = this.dataset;
    if (form && productId && form.getAttribute('data-product-id') !== productId) return;

    event.preventDefault();

    const formId = this.dataset.klaviyoFormId;
    if (!formId) return;

    await this.#closeEnclosingDialog();
    this.#openKlaviyoForm(formId);
  };

  /** Fired by caldera-subscription.js whenever the Subscribe / Buy Once choice settles. */
  #onSubscriptionChange = () => {
    this.#applyGate();
  };

  /** @param {Event} event - A ProductSelectEvent from @shopify/events. */
  #onProductSelect = (event) => {
    // Swatch pickers on product cards fire the same event and never carry this
    // buy box's markup.
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    const typed = /** @type {{ detail?: { variantId?: string | number }, promise?: Promise<{ detail?: { html?: Document, productId?: string } }> }} */ (
      /** @type {unknown} */ (event)
    );

    // Fired synchronously, before the section refetch starts. Shut the buy box
    // now rather than after the round trip.
    const variantId = typed.detail?.variantId;
    const gatedIds = this.#gatedIds();
    if (variantId != null && gatedIds.size > 0) {
      this.dataset.active = gatedIds.has(String(variantId)) ? 'true' : 'false';
      this.#applyGate(variantId);
    }

    const { promise } = typed;
    if (!promise) return;

    promise
      .then(async ({ detail }) => {
        const html = detail?.html;
        if (!html) return;

        const { productId } = detail ?? {};
        if (productId && this.dataset.productId && productId !== this.dataset.productId) return;

        const incoming =
          html.querySelector(`caldera-sold-out-notice[data-block-id="${this.dataset.blockId}"]`) ??
          html.querySelector('caldera-sold-out-notice');
        if (!(incoming instanceof HTMLElement)) return;

        this.dataset.active = incoming.dataset.active ?? 'false';
        this.dataset.subscriptionRequired = incoming.dataset.subscriptionRequired ?? 'false';

        const content = this.refs.content ?? this.querySelector('[ref="content"]');
        const incomingContent = incoming.querySelector('[ref="content"]');
        if (content && incomingContent) morph(content, incomingContent);

        // product-form.js resolves the same promise and re-shows the express
        // buttons from its own handler. Listener order between the two isn't
        // guaranteed, so yield a frame and re-assert rather than racing it.
        await new Promise((resolve) => requestAnimationFrame(resolve));
        this.#applyGate();
      })
      .catch(() => {
        // An aborted or failed variant fetch leaves the gate as it is; guessing
        // at a new state could expose a buy button we meant to withhold.
      });
  };
}

if (!customElements.get('caldera-sold-out-notice')) {
  customElements.define('caldera-sold-out-notice', CalderaSoldOutNotice);
}
